import { Table } from "@tanstack/react-table";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

import { Meta } from "@/@types/paginated";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect } from "react";
import { useGenerateTermFilterActions } from "../filter/hooks/actions";
import { useFilterState } from "../filter/hooks/filterState";

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  meta: Meta;
}

export function DataTablePagination<TData>({
  table,
  meta,
}: DataTablePaginationProps<TData>) {
  const filterState = useFilterState();
  const { updateFilter } = useGenerateTermFilterActions();
  const { filters } = filterState || { filters: {} };

  const currentPerPage = filters?.meta?.perPage || meta.perPage || 10;
  const currentPage = filters?.meta?.page || meta.currentPage || 1;

  useEffect(() => {
    table.setPageSize(currentPerPage);
  }, [currentPerPage, table]);

  return (
    <div className="flex flex-col space-y-4 px-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
      <div className="flex-1 text-sm text-muted-foreground text-center sm:text-left">
        {table.getFilteredRowModel().rows.length} linha(s) encontrada(s).
      </div>
      <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-6 lg:space-x-8">
        <div className="flex items-center justify-center space-x-2 sm:justify-start">
          <p className="text-sm font-medium">Linhas por página</p>
          <Select
            value={`${currentPerPage}`}
            onValueChange={(value) => {
              updateFilter({
                key: "meta",
                value: {
                  perPage: Number(value),
                  page: 1,
                },
              });
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={currentPerPage} />
            </SelectTrigger>
            <SelectContent side="top">
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-center text-sm font-medium sm:w-[100px]">
          Página {meta.currentPage} de {meta.lastPage}
        </div>
        <div className="flex items-center justify-center space-x-2 sm:justify-start">
          <Button
            variant="outline"
            className="h-8 w-8 p-0 sm:flex lg:flex"
            onClick={() => {
              updateFilter({
                key: "meta",
                value: {
                  page: 1,
                  perPage: currentPerPage,
                },
              });
            }}
            disabled={meta.prev === null}
          >
            <span className="sr-only">Ir para a primeira página</span>
            <ChevronsLeft />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => {
              updateFilter({
                key: "meta",
                value: {
                  page: Math.max(1, currentPage - 1),
                  perPage: currentPerPage,
                },
              });
            }}
            disabled={meta.prev === null}
          >
            <span className="sr-only">Ir para a página anterior</span>
            <ChevronLeft />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => {
              updateFilter({
                key: "meta",
                value: {
                  page: currentPage + 1,
                  perPage: currentPerPage,
                },
              });
            }}
            disabled={meta.next === null}
          >
            <span className="sr-only">Ir para a próxima página</span>
            <ChevronRight />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0 sm:flex lg:flex"
            onClick={() => {
              updateFilter({
                key: "meta",
                value: {
                  page: meta.lastPage,
                  perPage: currentPerPage,
                },
              });
            }}
            disabled={meta.next === null}
          >
            <span className="sr-only">Ir para a última página</span>
            <ChevronsRight />
          </Button>
        </div>
      </div>
    </div>
  );
}